import * as React from "react"
import { Loader2, PenLine, TriangleAlert } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

const MIN_REASON_LENGTH = 10

export interface ReasonDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  /** Resumen de lo que se va a cambiar: antes / despues. */
  summary?: React.ReactNode
  confirmLabel?: string
  variant?: "default" | "destructive"
  /** Motivos frecuentes: aceleran sin volver opcional el campo. */
  suggestions?: string[]
  pending?: boolean
  onConfirm: (reason: string) => void | Promise<void>
}

/**
 * Motivo obligatorio en TODA edicion. Es la regla transversal del sistema:
 * cada cambio queda versionado con autor, fecha y motivo en el audit log.
 * Este dialogo es el unico camino para escribir; no hay guardado silencioso.
 *
 * El contador de caracteres no es decoracion: convierte "obligatorio, minimo
 * 10" en una barra que se llena, para que el usuario sepa cuando puede guardar
 * sin tener que intentarlo y fallar.
 */
export function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  summary,
  confirmLabel = "Guardar con motivo",
  variant = "default",
  suggestions,
  pending = false,
  onConfirm,
}: ReasonDialogProps) {
  const [reason, setReason] = React.useState("")
  const [touched, setTouched] = React.useState(false)

  React.useEffect(() => {
    if (!open) {
      setReason("")
      setTouched(false)
    }
  }, [open])

  const trimmed = reason.trim()
  const invalid = trimmed.length < MIN_REASON_LENGTH
  const progress = Math.min(1, trimmed.length / MIN_REASON_LENGTH)

  async function handleConfirm() {
    setTouched(true)
    if (invalid) return
    await onConfirm(trimmed)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        {summary ? (
          <div
            className={cn(
              "spine rounded-md border bg-surface-sunken p-2.5 text-xs leading-[1.15rem]",
              variant === "destructive"
                ? "[--spine-color:hsl(var(--destructive))]"
                : "[--spine-color:hsl(var(--brand))]",
            )}
          >
            {summary}
          </div>
        ) : null}

        <div className="space-y-1.5">
          <Label htmlFor="reason-input">
            {t("Motivo")}{" "}<span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="reason-input"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setTouched(true)}
            placeholder={t("Que se corrige y por que. Queda en el audit log con tu nombre y la fecha.")}
            className="min-h-[5rem]"
            aria-invalid={touched && invalid}
            aria-describedby="reason-help"
            autoFocus
          />

          <div className="flex items-center gap-2">
            {/* Umbral visible: se llena hasta el minimo y despues desaparece. */}
            <div className="h-[3px] flex-1 overflow-hidden rounded-full bg-border">
              <div
                className={cn(
                  "h-full transition-[width,background-color] duration-150",
                  invalid ? "bg-warning" : "bg-success",
                )}
                style={{ width: `${progress * 100}%` }}
              />
            </div>
            <span
              id="reason-help"
              className={cn(
                "shrink-0 text-2xs tabular-nums",
                touched && invalid ? "font-medium text-destructive" : "text-muted-foreground",
              )}
            >
              {invalid
                ? `${MIN_REASON_LENGTH - trimmed.length} caracteres mas`
                : `${trimmed.length} caracteres`}
            </span>
          </div>

          {suggestions?.length ? (
            <div className="flex flex-wrap gap-1 pt-1">
              <span className="self-center text-2xs text-muted-foreground">{t("Frecuentes:")}</span>
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => setReason(suggestion)}
                  className={cn(
                    "max-w-full truncate rounded-sm border border-input bg-surface px-1.5 py-0.5 text-2xs text-muted-foreground",
                    "transition-colors hover:border-border-strong hover:bg-accent hover:text-foreground",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  )}
                  title={suggestion}
                >
                  <PenLine className="mr-1 inline h-2.5 w-2.5" />
                  {suggestion}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {variant === "destructive" ? (
          <p className="flex items-start gap-1.5 rounded-md border border-warning/35 bg-warning-soft p-2 text-2xs leading-4">
            <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0 text-warning" />
            {t("Los periodos cerrados no se editan: la correccion va por asiento de reversion en un periodo posterior.")}</p>
        ) : null}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={pending}>
            {t("Cancelar")}</Button>
          <Button
            size="sm"
            variant={variant === "destructive" ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={invalid || pending}
          >
            {pending ? <Loader2 className="animate-spin" /> : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
